import React, { useEffect, useRef, useState } from 'react';
import { getFileUploadSignedUrl } from 'store/files/services/getFileUploadSignedUrl.service';
import { createFilePermissions } from 'store/filepermissions/thunks/createFilePermissions';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Config from 'Config';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ProgressBarDialog from 'components/ProgressBarDialog/ProgressBarDialog';

/**
 * Custom hook for uploading files to S3 and creating file permissions for the uploaded files.
 */
export const useUploadFile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cancelTokenSource = useRef(axios.CancelToken.source());

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [spinnerSnackbarOpen, setSpinnerSnackbarOpen] = useState(false);
  const [showProgressBarDialog, setShowProgressBarDialog] = useState(false);
  const [progressBarValue, setProgressBarValue] = useState(null);
  const [progressBarFullValue, setProgressBarFullValue] = useState(null);
  const [progressBarFileName, setProgressBarFileName] = useState(null);
  const [progressBarFileCount, setProgressBarFileCount] = useState(null);
  const [progressBarFullCount, setProgressBarFullCount] = useState(null);

  const [progressBarValues, setProgressBarValues] = useState(null);

  const isCancelled = useRef(false);

  const onCancelAdding = () => {
    setShowProgressBarDialog(false);
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
    isCancelled.current = true;
    setProgressBarDefaults();
  };

  const onCloseProgressBar = () => {
    setShowProgressBarDialog(false);
    setProgressBarDefaults();
  };

  const setProgressBarDefaults = () => {
    setProgressBarValue(null);
    setProgressBarFullValue(null);
    setProgressBarFileName(null);
    setProgressBarFileCount(null);
    setProgressBarFullCount(null);
  };

  useEffect(() => {
    setProgressBarValues({
      progressBarValue,
      progressBarFullValue,
      progressBarFileName,
      progressBarFileCount,
      progressBarFullCount,
      showProgressBarDialog,
    });
  }, [
    progressBarValue,
    progressBarFullValue,
    progressBarFileName,
    progressBarFileCount,
    progressBarFullCount,
    showProgressBarDialog,
  ]);

  const ITEM_TYPES = Object.freeze({
    BULLETIN: 'BULLETIN',
    // ADD MORE WHEN NEEDED
  });

  /**
   * Uploads a file to S3 without creating filepermission. Use some item id as the filename in s3 to link the file to an item (e.g. orientationMaterialId)
   * @param {object} params - Object containing parameters for the function
   * @param {File} params.file - File to be uploaded
   * @param {string} params.path - Path to save the file in S3
   * @param {string} params.fileName - If you wish to specify the name of the file to be uploaded (e.g. `${fileName}.jpg`) (optional)
   * @param {boolean} params.showProgress - Show progress bar (optional)
   * @param {boolean} params.ignoreExtension - Ignore the extension of the file (optional)
   * @returns - retuns the result of the upload
   */
  async function uploadFileToS3({
    file,
    path,
    fileName,
    showProgress,
    ignoreExtension,
  }) {
    if (!file) {
      return null;
    }

    if (!path) {
      console.error('No path provided');
      return null;
    }

    // control if the progress bar should be shown
    if (showProgress) setShowProgressBarDialog(true);

    setProgressBarFullCount(1);

    console.log('file:', file);
    setProgressBarFileCount(1);
    let name = file?.name;
    let fileType = file.type;
    if (!name) {
      name = fileType.split('/')[1];
    }
    let extension = '';
    try {
      const name_parts = name.split('.');
      extension = name_parts[name_parts.length - 1];
    } catch {
      extension = '';
    }

    extension = extension.toLowerCase();

    fileName = fileName ? fileName : uuidv4();

    const fileNameWithExtension = `${fileName}.${extension}`;

    if (ignoreExtension) {
      path = `${path}/${fileName}`;
    } else {
      path = `${path}/${fileNameWithExtension}`;
    }

    setProgressBarFileName(name);

    let result = null;

    try {
      const signedUrl = await getFileUploadSignedUrl({
        fileName: fileName,
        contentType: fileType,
        filePath: path,
      });

      cancelTokenSource.current = axios.CancelToken.source();
      const options = {
        cancelToken: cancelTokenSource.current.token,
        headers: {
          'Content-Type': fileType,
        },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const percent = Math.floor((loaded * 100) / total);
          setProgressBarValue(Math.round((loaded / 1000000) * 10) / 10);
          setProgressBarFullValue(Math.round((total / 1000000) * 10) / 10);
          console.log(`${loaded}kb of ${total}kb | ${percent}%`); // just to see whats happening in the console
        },
      };
      result = await axios.put(signedUrl, file, options);
      // console.log('result', result);
      if (result.status !== 200) {
        setProgressBarDefaults();
        console.log(`${t('alerts.s3_post_failed')}`);
        return;
      }
    } catch (err) {
      console.log(err);
      console.log(`${t('alerts.s3_post_cancelled')}`);
    }

    setProgressBarDefaults();
    setShowProgressBarDialog(false);

    // If the upload was cancelled, return null
    if (isCancelled.current) {
      isCancelled.current = false;
      return null;
    } else {
      isCancelled.current = false;
      return result;
    }
  }

  /**
 * Uploads a file to S3 without creating filepermission. Use some item id as the filename in s3 to link the file to an item (e.g. orientationMaterialId)
 * @param {object} params - Object containing parameters for the function
 * @param {File} params.file - File to be uploaded
 * @param {string} params.path - Path to save the file in S3
 * @param {string} params.fileName - If you wish to specify the name of the file to be uploaded (e.g. `${fileName}.jpg`) (optional)
 * @param {boolean} params.ignoreExtension - Ignore the extension of the file (optional)
 * @returns - returns the result of the upload
 */
  async function uploadFileToS3Simple({
    file,
    path,
    fileName,
    ignoreExtension,
  }) {
    if (!file) {
      return null;
    }

    if (!path) {
      console.error('No path provided');
      return null;
    }

    let name = file?.name;
    let fileType = file.type;
    if (!name) {
      name = fileType.split('/')[1];
    }
    let extension = '';
    try {
      const name_parts = name.split('.');
      extension = name_parts[name_parts.length - 1];
    } catch {
      extension = '';
    }

    extension = extension.toLowerCase();

    fileName = fileName ? fileName : uuidv4();

    const fileNameWithExtension = `${fileName}.${extension}`;

    if (ignoreExtension) {
      path = `${path}/${fileName}`;
    } else {
      path = `${path}/${fileNameWithExtension}`;
    }

    let result = null;

    try {
      const signedUrl = await getFileUploadSignedUrl({
        fileName: fileName,
        contentType: fileType,
        filePath: path,
      });

      cancelTokenSource.current = axios.CancelToken.source();
      const options = {
        cancelToken: cancelTokenSource.current.token,
        headers: {
          'Content-Type': fileType,
        },
      };
      result = await axios.put(signedUrl, file, options);

      if (result.status !== 200) {
        console.log(`${t('alerts.s3_post_failed')}`);
        return null;
      }
    } catch (err) {
      console.log(err);
      console.log(`${t('alerts.s3_post_cancelled')}`);
      return null;
    }

    // If the upload was cancelled, return null
    if (isCancelled.current) {
      isCancelled.current = false;
      return null;
    } else {
      isCancelled.current = false;
      return result;
    }
  }

  /**
   * Uploads files to S3 and creates file permissions for the uploaded files.
   * @param {object} params - Object containing parameters for the function
   * @param {array} params.files - Array of files to be uploaded (e.g. [{file: file, id: id}])
   * @param {string} params.mainFolder - Main folder in bucket where to upload files, path created will be mainFolder/businessId/projectKey/folder/filename
   * @param {object} params.project - Project object
   * @param {object} params.user - User object
   * @param {string} params.itemId - Item id to link the file to (e.g. bulletin_id) (optional)
   * @param {string} params.itemType - Item type to link the file to (e.g. bulletin) (optional)
   * @param {string} params.uploadSource - Source of the upload (optional)
   * @param {boolean} params.showProgress - Show progress bar (optional)
   * @returns - Array of created file permissions
   */
  async function uploadFilesAndCreateFilePermissions({
    files,
    mainFolder,
    project,
    user,
    itemId,
    itemType,
    uploadSource,
    showProgress,
  }) {
    if (files.length === 0) {
      return null;
    }

    if (!mainFolder) {
      console.error('No main folder provided');
      return null;
    }

    if (!project) {
      console.error('Project not provided');
      return null;
    }

    if (!project.key) {
      console.error('project.key missing from project');
      return null;
    }

    if (!project.projectKey) {
      console.error('project.projectKey missing from project');
      return null;
    }

    if (!project.business_id) {
      console.error('project.business_id missing from project');
      return null;
    }

    if (!user) {
      console.error('No user provided');
      return null;
    }

    const formattedFiles = formatFiles(files);
    // control if the progress bar should be shown
    if (showProgress) setShowProgressBarDialog(true);

    setProgressBarFullCount(files.length);
    let i = 0;
    let filePermissions = [];

    for (let i = 0; i < files.length; i++) {
      const file = formattedFiles[i].file;
      const id = formattedFiles[i].id;
      setProgressBarFileCount(i + 1);
      let name = file?.name ? file.name : file.fileName;
      let fileType = file.type;
      let extension = '';
      try {
        const name_parts = name.split('.');
        extension = name_parts[name_parts.length - 1];
      } catch {
        extension = '';
      }

      extension = extension.toLowerCase();

      setProgressBarFileName(name);

      try {
        const filePermissionId = uuidv4();
        const projectKey = project.projectKey;
        const businessId = project.business_id;

        let filePermission = {
          id: filePermissionId,
          mirrorUser: user.id,
          name: name,
          project: project.id,
          file: null,
          key: projectKey,
          parentKey: businessId,
          businessId: businessId,
          type: 'FILE',
          description: '',
          uploadSource: uploadSource || 'add-file',
        };

        // Item id is used to link the file to a specific item (e.g. bulletin etc.)
        // for example, when adding a photo to a bulletin, the bulletin id is used as item_id
        if (itemId) {
          filePermission.itemId = itemId;
        }

        if (itemType) {
          filePermission.itemType = itemType;
        }

        // const filesDomain = process.env.REACT_APP_FILES_DOMAIN;
        const filename = `${id}.${extension}`;
        let folder = 'attachments';

        if (fileType.includes('image')) {
          filePermission.hasMediumSize = true;
          folder = 'images';
        } else if (fileType.includes('video')) {
          folder = 'images';
        }

        const filePermissionKey = `${mainFolder}/${businessId}/${projectKey}/${folder}/${filename}`;
        const filePermissionParentKey = `${mainFolder}/${businessId}/${projectKey}/${folder}`;

        // const path = `${process.env.REACT_APP_S3_PROJECT_BANK_DIR}/${businessId ? businessId + '_' : ''
        //     }${projectKey}_images`;

        try {
          const signedUrl = await getFileUploadSignedUrl({
            fileName: filename,
            contentType: fileType,
            filePath: filePermissionKey,
          });

          let newFile;
          cancelTokenSource.current = axios.CancelToken.source();
          const options = {
            cancelToken: cancelTokenSource.current.token,
            headers: {
              'Content-Type': fileType,
            },
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percent = Math.floor((loaded * 100) / total);
              setProgressBarValue(Math.round((loaded / 1000000) * 10) / 10);
              setProgressBarFullValue(Math.round((total / 1000000) * 10) / 10);
              console.log(`${loaded}kb of ${total}kb | ${percent}%`); // just to see whats happening in the console
            },
          };
          const result = await axios.put(
            signedUrl,
            newFile ? newFile : file,
            options
          );
          // console.log('result', result);
          if (result.status !== 200) {
            setProgressBarDefaults();
            console.log(`${t('alerts.s3_post_failed')}`);
            return;
          }

          // const filePermissionKey = `${Config.S3_PROJECT_BANK_DIR}/${businessId ? businessId + '/' : ''
          //     }${projectKey}/images/${filename}`;
          // const filePermissionParentKey = `${Config.S3_PROJECT_BANK_DIR}/${businessId ? businessId + '/' : ''
          //     }${projectKey}/images`;

          filePermission.file = {
            bucket: Config.S3_BUCKET,
            key: filePermissionKey,
            mimeType: fileType,
            region: Config.AWS_REGION,
          };
          filePermission.key = filePermissionKey;
          filePermission.parentKey = filePermissionParentKey;
          filePermission.path = `${mainFolder}/${project.key}/${folder}/${filename}`;
          filePermission.parentPath = `${mainFolder}/${project.key}/${folder}`;
          // filePermission.path = `${Config.S3_PROJECT_BANK_DIR}/${project.key ? project.key : ''
          //     }/images/${filename}`;
          // filePermission.parentPath = `${Config.S3_PROJECT_BANK_DIR}/${project.key ? project.key : ''
          //     }/images`;

          // Add filepermission to an array of filepermissions
          filePermissions.push(filePermission);
        } catch (err) {
          console.log(err);
          console.log(`${t('alerts.s3_post_cancelled')}`);
        }
      } catch (err) {
        console.log(err);
        break;
      }

      if (files.size > 1) setProgressBarValue(files.length);
    }

    setProgressBarDefaults();
    setShowProgressBarDialog(false);
    // if (files.length > 0 && saveAndSend) {
    //     setSnackbarOpen(true);
    // }
    if (files.length > 0) {
      setSnackbarOpen(true);
    }
    let promise = null;

    // If the upload was cancelled, return null
    if (isCancelled.current) {
      isCancelled.current = false;
      return promise;
    } else {
      promise = createFilePermissionsForImages(filePermissions);
      isCancelled.current = false;
    }
    return promise;
  }

  /**
     * Uploads files to S3 and creates file permissions for the uploaded files.
     * @param {object} params - Object containing parameters for the function
     * @param {array} params.file - Array of files to be uploaded (e.g. [{file: file, id: id}])
     * @param {object} params.filePermission - File permission object to be created:
     * ### File permission object:
     * // const filePermissionExample = {
    //     id: uuidv4(),
    //     mirrorUser: user.id,
    //     name: name,
    //     project: project.id,
    //     file: null,
    //     key: path,
    //     parentKey: pathWithoutFileName,
    //     parentPath: pathWithoutFileName,
    //     type: 'FILE',
    // }
    * @param {string?} params.path - Path in bucket where to upload files (e.g. project-bank/businessId/projectKey/folder/filename), if not provided, the path will be they "key" provided in filePermission 
     * @param {string?} params.fileName - If you wish to specify the name of the file to be uploaded (e.g. `${fileName}.jpg`) (optional)
     * @param {boolean} params.showProgress - Show progress bar (optional)
     * @param {boolean} params.ignoreExtension - Ignore the extension of the file (optional)
     * @returns - Array of created file permissions
     */
  async function uploadFileAndFilePermission({
    file,
    filePermission,
    path,
    fileName,
    showProgress,
    ignoreExtension,
  }) {
    if (!file) {
      console.error('File not provided');
      return null;
    }

    if (!filePermission) {
      console.error('Filepermission not provided');
      return null;
    }

    const formattedFile = formatFile(file);
    // control if the progress bar should be shown
    // if (showProgress) setShowProgressBarDialog(true);

    // setProgressBarFullCount(1);
    let i = 0;
    let filePermissions = [];

    const tempFile = formattedFile.file;
    const id = formattedFile.id;
    // setProgressBarFileCount(i + 1);
    let name = tempFile?.name ? tempFile.name : tempFile.fileName;
    let fileType = tempFile.type;
    let extension = '';
    try {
      const name_parts = name.split('.');
      extension = name_parts[name_parts.length - 1];
    } catch {
      extension = '';
    }

    extension = extension.toLowerCase();

    // setProgressBarFileName(name);

    try {
      let filename;
      if (ignoreExtension) {
        filename = fileName ? `${fileName}` : `${id}`;
      } else {
        filename = fileName ? `${fileName}.${extension}` : `${id}.${extension}`;
      }

      if (fileType.includes('image')) {
        filePermission.hasMediumSize = true;
      }

      try {
        const signedUrl = await getFileUploadSignedUrl({
          fileName: filename,
          contentType: fileType,
          filePath: path,
        });

        let newFile;
        cancelTokenSource.current = axios.CancelToken.source();
        const options = {
          cancelToken: cancelTokenSource.current.token,
          headers: {
            'Content-Type': fileType,
          },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percent = Math.floor((loaded * 100) / total);
            setProgressBarValue(Math.round((loaded / 1000000) * 10) / 10);
            setProgressBarFullValue(Math.round((total / 1000000) * 10) / 10);
            console.log(`${loaded}kb of ${total}kb | ${percent}%`); // just to see whats happening in the console
          },
        };
        const result = await axios.put(
          signedUrl,
          newFile ? newFile : file,
          options
        );
        // console.log('result', result);
        if (result.status !== 200) {
          setProgressBarDefaults();
          console.log(`${t('alerts.s3_post_failed')}`);
          return;
        }

        filePermission.file = {
          bucket: Config.S3_BUCKET,
          key: path ? path : filePermission.key,
          mimeType: fileType,
          region: Config.AWS_REGION,
        };

        // Add filepermission to an array of filepermissions
        filePermissions.push(filePermission);
      } catch (err) {
        console.log(err);
        console.log(`${t('alerts.s3_post_cancelled')}`);
      }
    } catch (err) {
      console.log(err);
    }

    // if (files.size > 1) setProgressBarValue(files.length);

    setProgressBarDefaults();
    setShowProgressBarDialog(false);
    // if (files.length > 0 && saveAndSend) {
    //     setSnackbarOpen(true);
    // }
    // if (files.length > 0) {
    //     setSnackbarOpen(true);
    // }
    let promise = null;

    // If the upload was cancelled, return null
    if (isCancelled.current) {
      isCancelled.current = false;
      return promise;
    } else {
      promise = createFilePermissionsForImages(filePermissions);
      isCancelled.current = false;
    }
    return promise;
  }

  /**
   * Creates file permissions for all images.
   * @param {array} filePermissions - Array of file permissions to be created.
   * @returns {array} - array of created file permissions
   */
  async function createFilePermissionsForImages(filePermissions) {
    if (!filePermissions) {
      return;
    }

    const filePermissionToReturn = dispatch(
      createFilePermissions(filePermissions)
    ).then((res) => {
      return res.payload.createdFilePermissions;
    });

    return filePermissionToReturn;
  }

  // import ProgressBarDialog from 'components/ProgressBarDialog/ProgressBarDialog';
  {
    /* {progressBarValues && progressBarValues.showProgressBarDialog ?
          <ProgressBarDialog
            header={t('common.loading_images_to_lixani')}
            onCancelAdding={onCancelAdding}
            onClose={onCloseProgressBar}
            progressBarValues={progressBarValues}
          />
          : null} */
  }

  // UploadProgressDialog FLICKERS FOR SOME REASON (can try and fix it later, but for now it's not used in any component)
  // Use commented code above instead in the component where this hook is used
  const UploadProgressDialog = (props) => {
    const { t } = useTranslation();
    return (
      <>
        <ProgressBarDialog
          header={t('common.loading_images_to_lixani')}
          progressBarValues={progressBarValues}
          onCancelAdding={onCancelAdding}
          onClose={onCloseProgressBar}
        />
      </>
    );
  };

  /**
   * Convert to correct format for uploading
   * @param {array} files - Array of files
   * @returns - Array of fileobjects in correct format for uploading
   */
  const formatFiles = (files) => {
    if (!files || !files.length) return;

    const filesArrayWithId = Array.from(files).map((file, index) => ({
      file,
      id: uuidv4(),
    }));

    return filesArrayWithId;
  };

  /**
   * Convert to correct format for uploading
   * @param {file} file - File
   * @returns - fileobject to be uploaded
   */
  const formatFile = (file) => {
    if (!file) return;

    return {
      file,
      id: uuidv4(),
    };
  };

  return {
    uploadFileToS3,
    uploadFileToS3Simple,
    uploadFilesAndCreateFilePermissions,
    uploadFileAndFilePermission,
    onCancelAdding,
    onCloseProgressBar,
    setSnackbarOpen,
    setSpinnerSnackbarOpen,
    progressBarValues,
    UploadProgressDialog,
    ITEM_TYPES,
    formatFiles,
  };
};
